import React, { useCallback, useMemo, useState } from "react";
import { MoveFocusInside } from "react-focus-lock";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  IconButton,
  ListItem,
  List,
  Menu,
  SlideLeftRightTransition,
  ResizeTransition,
  AddCrossIcon,
  MenuHeader,
  ArrowRightIcon,
  Caption1,
  SkeletonLoader,
} from "@activecollab/components";
import moment from "moment";
import PropTypes from "prop-types";
import { Submenu } from "~/components/tasks/TaskOptions/Submenus";
import { getPersonalizedTimeFormat } from "~/store/selectors/selector.configOptions";

const WIDTH = 260;

export const QuickReminderMenu = ({
  options = [],
  additionalSubmenuOptions = [],
  customEnabled = true,
  onSubmit,
  target = null,
  isLoading = false,
  onOpen,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState("index");
  const [isLeft, setIsLeft] = useState(true);
  const [height, setHeight] = useState(null);
  const [enteredMenu, setEnteredMenu] = useState("index");
  const [submenuTitle, setSubmenuTitle] = useState("");
  const [subscribers, setSubscribers] = useState([]);
  const formatTime = useSelector(getPersonalizedTimeFormat);

  const submenuOptions = useMemo(() => {
    const result = [];
    let date;
    const now = moment();
    if (now.isBefore(moment("19:00", "HH:mm"))) {
      if (now.isBefore(moment("14:00", "HH:mm"))) {
        date = moment("17:00", "HH:mm");
        result.push({
          id: "later-today",
          name: t("Later Today"),
          time_label: date.format(formatTime),
          remind_on: date.unix(),
        });
      } else {
        date = moment("20:00", "HH:mm");
        result.push({
          id: "later-today",
          name: t("Later Today"),
          time_label: date.format(formatTime),
          remind_on: date.unix(),
        });
      }
    }

    date = moment("07:00", "HH:mm").add(1, "day");
    result.push({
      id: "tomorrow",
      name: t("Tomorrow"),
      time_label: `${date.format("ddd")}, ${date.format(formatTime)}`,
      remind_on: moment("07:00", "HH:mm").add(1, "day").unix(),
    });

    if (now.day()) {
      date = moment().add(1, "week").startOf("week").add(7, "hours");
      result.push({
        id: "next-week",
        name: t("Next Week"),
        time_label: `${date.format("ddd")}, ${date.format(formatTime)}`,
        remind_on: date.unix(),
      });
    }

    if (additionalSubmenuOptions.length > 0) {
      additionalSubmenuOptions.forEach((additionalOption) => {
        result.push(additionalOption);
      });
    }

    if (customEnabled) {
      result.push({
        id: "custom",
        name: t("Custom") + "...",
        time_label: null,
        remind_on: moment("07:00", "HH:mm").add(1, "day").unix(),
      });
    }

    return result;
  }, [additionalSubmenuOptions, customEnabled, formatTime, t]);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleHeight = useCallback((element) => {
    setHeight(element.offsetHeight);
  }, []);

  const handleEnteredIndex = useCallback(() => {
    setEnteredMenu("index");
    setHeight(null);
  }, []);

  const resetMenu = useCallback(() => {
    setMenu("index");
    setEnteredMenu("index");
    setIsLeft(false);
    setHeight(null);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    resetMenu();
  }, [resetMenu]);

  const handleEntered = useCallback(() => {
    setEnteredMenu("dates");
  }, []);

  const handleSubmenu = useCallback((title, subscribers) => {
    setSubscribers(subscribers);
    setSubmenuTitle(title);
    setMenu("dates");
    setIsLeft(true);
  }, []);

  const handleClick = useCallback(
    (option) => {
      if (typeof option?.onClick === "function") {
        option.onClick();
        setOpen(false);
        return;
      }
      handleSubmenu(option.name, option.subscribers);
    },
    [handleSubmenu]
  );

  const handleSubmit = useCallback(
    (option) => {
      const { name, time_label, ...rest } = option;
      if (typeof onSubmit === "function") {
        onSubmit({
          ...rest,
          subscribers,
        });
      }
      handleClose();
    },
    [handleClose, onSubmit, subscribers]
  );

  const menuTarget = useMemo(() => {
    if (target) {
      return React.cloneElement(target, { onClick: handleOpen, active: open });
    }
    return (
      <IconButton
        variant="text gray"
        active={open}
        onClick={handleOpen}
      >
        <AddCrossIcon />
      </IconButton>
    );
  }, [handleOpen, open, target]);

  const handleOnOpen = useCallback(() => {
    if (typeof onOpen === "function") {
      onOpen();
    }
  }, [onOpen]);

  return (
    <>
      <Menu
        open={open}
        className="tw-overflow-hidden tw-relative"
        onClose={handleClose}
        target={menuTarget}
        onOpen={handleOnOpen}
      >
        <ResizeTransition
          in
          style={{ height: height ? `${height}px` : "fit-content", width: `${WIDTH}px` }}
        >
          <div style={{ height: height ? `${height}px` : "fit-content", width: `${WIDTH}px` }}>
            <SlideLeftRightTransition
              in={menu === "index"}
              direction={isLeft ? "left" : "right"}
              onEnter={handleHeight}
              onEntered={handleEnteredIndex}
            >
              <MoveFocusInside disabled={enteredMenu !== "index"}>
                <MenuHeader title={`${t("Remind")}...`} />
                {isLoading ? (
                  <List
                    className="tw-py-2"
                    style={{ width: WIDTH }}
                  >
                    {[1, 2, 3].map((option, index) => (
                      <ListItem key={index}>
                        <div className="tw-h-7 tw-w-full tw-flex tw-items-center">
                          <SkeletonLoader />
                        </div>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <List
                    className="tw-py-2"
                    style={{ width: WIDTH }}
                  >
                    {options.map((option) => (
                      <ListItem
                        key={option.id}
                        onClick={() => handleClick(option)}
                      >
                        <div className="tw-w-full tw-flex tw-items-center tw-justify-between">
                          {option.name}
                          {typeof option?.onClick === "function" ? null : <ArrowRightIcon />}
                        </div>
                      </ListItem>
                    ))}
                  </List>
                )}
              </MoveFocusInside>
            </SlideLeftRightTransition>
            <SlideLeftRightTransition
              in={menu === "dates"}
              direction={isLeft ? "left" : "right"}
              onEnter={handleHeight}
              onEntered={handleEntered}
            >
              <MoveFocusInside disabled={enteredMenu !== "dates"}>
                <Submenu
                  title={submenuTitle}
                  onBack={resetMenu}
                  style={{
                    width: WIDTH,
                  }}
                  onHeightChange={setHeight}
                >
                  <List
                    className="tw-py-2"
                    style={{ width: WIDTH }}
                  >
                    {submenuOptions.map((option) => (
                      <ListItem
                        key={option.id}
                        onClick={() => handleSubmit(option)}
                      >
                        <div
                          className="tw-w-full tw-flex tw-items-center tw-justify-between"
                          data-testid="time-label"
                        >
                          {option.name}
                          <Caption1 color="tertiary">{option.time_label}</Caption1>
                        </div>
                      </ListItem>
                    ))}
                  </List>
                </Submenu>
              </MoveFocusInside>
            </SlideLeftRightTransition>
          </div>
        </ResizeTransition>
      </Menu>
    </>
  );
};

QuickReminderMenu.propTypes = {
  options: PropTypes.array,
  additionalSubmenuOptions: PropTypes.array,
  customEnabled: PropTypes.bool,
  parentId: PropTypes.number,
  parentType: PropTypes.string,
  onSubmit: PropTypes.func,
  target: PropTypes.node,
  isLoading: PropTypes.bool,
  onOpen: PropTypes.func,
};
